import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Stack,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Snackbar,
    Alert,
    IconButton
} from '@mui/material';
import {
    ArrowLeft,
    X,
    AlertTriangle,
    Send
} from 'lucide-react';

// Specific mock data map for known hosts, with smart dynamic generator for other hosts
const HOST_DETAILS_MAP = {
    "host-001": {
        id: "host-001",
        name: "Kerala Care Centre",
        email: "kerala@example.com",
        contactPerson: "Priya Menon",
        phone: "+91 98••••120",
        location: "Kovalam, Kerala",
        joinedDate: "12 March 2026",
        slab: "30%",
        slabDescription: "Premium 30% — 20 pts in block D once the quality gate passes",
        payoutMethod: "Bank ••4821 · Razorpay Payouts · verified",
        avgResponseTime: "3h",
        status: "active",
        kyc: "verified",
        money: {
            inEscrow: "₹2,84,000",
            payableNow: "₹1,12,400",
            lifetimePaid: "₹1,96,000"
        },
        listings: [
            { id: "list-001", title: "10 Day's Retreat", status: "live", bookings: 11, gmv: "₹7.1L", rating: 4.8 },
            { id: "list-002", title: "5 Day Yoga — Varkala", status: "expiring", bookings: 3, gmv: "₹1.4L", rating: 4.2 }
        ],
        payouts: [
            { id: "payout-001", date: "05 Jul 2026", bookings: 3, gross: "₹1,84,000", commission: "₹55,200", paidOut: "₹1,28,800", status: "paid" },
            { id: "payout-002", date: "20 Jun 2026", bookings: 2, gross: "₹96,000", commission: "₹28,800", paidOut: "₹67,200", status: "paid" }
        ]
    },
    "host-002": {
        id: "host-002",
        name: "Om Yoga Ashram",
        email: "om.yoga@example.com",
        contactPerson: "Swami Shivananda",
        phone: "+91 94••••552",
        location: "Tapovan, Rishikesh",
        joinedDate: "18 January 2026",
        slab: "25%",
        slabDescription: "Featured 25% — Verified authentic yogic tradition",
        payoutMethod: "Bank ••3910 · Razorpay Payouts · verified",
        avgResponseTime: "1h",
        status: "active",
        kyc: "verified",
        money: {
            inEscrow: "₹1,65,000",
            payableNow: "₹82,500",
            lifetimePaid: "₹2,82,500"
        },
        listings: [
            { id: "list-003", title: "7-Day Kundalini & Meditation Retreat", status: "live", bookings: 8, gmv: "₹5.3L", rating: 4.5 }
        ],
        payouts: [
            { id: "payout-003", date: "01 Jul 2026", bookings: 4, gross: "₹2,65,000", commission: "₹66,250", paidOut: "₹1,98,750", status: "paid" }
        ]
    },
    "host-003": {
        id: "host-003",
        name: "Goa Wellness",
        email: "goa@example.com",
        contactPerson: "Marcus De Souza",
        phone: "+91 97••••881",
        location: "Anjuna, Goa",
        joinedDate: "04 February 2026",
        slab: "20%",
        slabDescription: "Boosted 20% — Beachside rejuvenation and sound healing",
        payoutMethod: "Bank ••7721 · Razorpay Payouts · verified",
        avgResponseTime: "6h",
        status: "slow replies",
        kyc: "verified",
        money: {
            inEscrow: "₹95,000",
            payableNow: "₹45,000",
            lifetimePaid: "₹1,80,000"
        },
        listings: [
            { id: "list-004", title: "7-Day Beachside Detox & Sound Healing", status: "live", bookings: 6, gmv: "₹3.2L", rating: 3.9 }
        ],
        payouts: [
            { id: "payout-004", date: "15 Jun 2026", bookings: 3, gross: "₹1,60,000", commission: "₹32,000", paidOut: "₹1,28,000", status: "paid" }
        ]
    }
};

const HostProfile = ({ host, onBack, onOpenKyc }) => {
    // Generate profile data either from map or based on clicked host
    const currentHost = (() => {
        if (!host) return HOST_DETAILS_MAP["host-001"];
        if (HOST_DETAILS_MAP[host.id]) {
            return {
                ...HOST_DETAILS_MAP[host.id],
                ...host,
                money: HOST_DETAILS_MAP[host.id].money,
                listings: HOST_DETAILS_MAP[host.id].listings,
                payouts: HOST_DETAILS_MAP[host.id].payouts
            };
        }
        // Fallback for any other host
        return {
            id: host.id || "host-001",
            name: host.name || "Kerala Care Centre",
            email: host.email || "kerala@example.com",
            contactPerson: "Priya Menon",
            phone: "+91 98••••120",
            location: host.location ? `${host.location}, India` : "Kovalam, Kerala",
            joinedDate: "12 March 2026",
            slab: host.slab || "30%",
            slabDescription: `Premium ${host.slab || '30%'} — 20 pts in block D once the quality gate passes`,
            payoutMethod: "Bank ••4821 · Razorpay Payouts · verified",
            avgResponseTime: "3h",
            status: host.status || "active",
            kyc: host.kyc || "verified",
            money: {
                inEscrow: host.gmv ? `₹${Math.round(parseFloat(host.gmv.replace(/[^\d.]/g, '') || 5) * 35000).toLocaleString('en-IN')}` : "₹2,84,000",
                payableNow: "₹1,12,400",
                lifetimePaid: "₹1,96,000"
            },
            listings: [
                { id: "list-001", title: "10 Day's Retreat", status: "live", bookings: host.bookings || 11, gmv: host.gmv || "₹7.1L", rating: host.rating || 4.8 },
                { id: "list-002", title: "5 Day Yoga — Varkala", status: "expiring", bookings: 3, gmv: "₹1.4L", rating: 4.2 }
            ],
            payouts: [
                { id: "payout-001", date: "05 Jul 2026", bookings: 3, gross: "₹1,84,000", commission: "₹55,200", paidOut: "₹1,28,800", status: "paid" },
                { id: "payout-002", date: "20 Jun 2026", bookings: 2, gross: "₹96,000", commission: "₹28,800", paidOut: "₹67,200", status: "paid" }
            ]
        };
    })();

    // Action Dialog States
    const [isSlabDialogOpen, setIsSlabDialogOpen] = useState(false);
    const [selectedSlab, setSelectedSlab] = useState(currentHost.slab || "30%");
    const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
    const [messageText, setMessageText] = useState("");
    const [isKycDialogOpen, setIsKycDialogOpen] = useState(false);
    const [isSuspendDialogOpen, setIsSuspendDialogOpen] = useState(false);

    // Toast Notification
    const [toastMessage, setToastMessage] = useState("");
    const [toastSeverity, setToastSeverity] = useState("success");
    const [toastOpen, setToastOpen] = useState(false);

    const handleSaveSlab = () => {
        setIsSlabDialogOpen(false);
        setToastMessage(`Commission slab updated to ${selectedSlab} for ${currentHost.name}.`);
        setToastSeverity("success");
        setToastOpen(true);
    };

    const handleSendMessage = () => {
        setIsMessageDialogOpen(false);
        setMessageText("");
        setToastMessage(`Message delivered to ${currentHost.name} via registered email.`);
        setToastSeverity("info");
        setToastOpen(true);
    };

    const handleSuspendToggle = () => {
        setIsSuspendDialogOpen(false);
        setToastMessage(`Host ${currentHost.name} status updated.`);
        setToastSeverity("warning");
        setToastOpen(true);
    };

    return (
        <Box sx={{ width: '100%', p: { xs: 2, sm: 2.5, md: 3.5 }, bgcolor: '#FAF8F5', minHeight: '100vh', boxSizing: 'border-box' }}>
            {/* ── Top Back Button ── */}
            <Button
                onClick={onBack}
                startIcon={<ArrowLeft size={16} />}
                disableElevation
                disableRipple
                sx={{
                    textTransform: 'none',
                    fontSize: '0.82rem',
                    fontWeight: 500,
                    color: '#64748B',
                    px: 0,
                    py: 0.5,
                    mb: 1.5,
                    '&:hover': {
                        bgcolor: 'transparent',
                        color: '#1E293B',
                    }
                }}
            >
                Back to Hosts
            </Button>

            {/* ── Page Title & Subtitle Note ── */}
            <Box sx={{ mb: 3 }}>
                <Typography
                    sx={{
                        color: '#1E293B',
                        fontWeight: 600,
                        fontSize: { xs: '1.4rem', md: '1.65rem' },
                        lineHeight: 1.2
                    }}
                >
                    Host Profile
                </Typography>
                <Typography sx={{ color: '#64748B', fontSize: '0.84rem', mt: 0.6, fontWeight: 400 }}>
                    One account per partner — whoever adds the retreat is the host · 12 active
                </Typography>
                <Typography sx={{ color: '#78716C', fontSize: '0.8rem', mt: 0.25, fontWeight: 400 }}>
                    Their centres and gurus live in Directory — this screen stays about the account, the money and the KYC
                </Typography>
            </Box>

            {/* ── Main Full-Width Flexbox Layout (Left Expanded, Right 2 Cards in Corner) ── */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2.5,
                    width: '100%',
                    alignItems: 'flex-start'
                }}
            >
                {/* ── LEFT SIDE: Host Overview + Listings + Payout History (Maximizes Width) ── */}
                <Box sx={{ flex: '1 1 0%', minWidth: 0, width: '100%' }}>
                    <Stack spacing={2.5}>
                        {/* 1. Host Overview Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, md: 2.5 },
                                borderRadius: '8px',
                                border: '1px solid #EAE5D9',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            {/* Host Title & Verified/Active Badge */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                                <Typography
                                    sx={{
                                        fontSize: '1.15rem',
                                        fontWeight: 600,
                                        color: '#2D1810',
                                    }}
                                >
                                    {currentHost.name}
                                </Typography>
                                <Box
                                    sx={{
                                        px: 1.2,
                                        py: 0.35,
                                        borderRadius: '16px',
                                        fontSize: '0.72rem',
                                        fontWeight: 500,
                                        bgcolor: '#EDF5EB',
                                        color: '#2E7D32',
                                        border: '1px solid #D6EAD2',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}
                                >
                                    <span>{currentHost.kyc || 'verified'}</span>
                                    <span>·</span>
                                    <span>{currentHost.status || 'active'}</span>
                                </Box>
                            </Box>

                            {/* Host Information Key-Value Rows */}
                            <Stack spacing={1.5}>
                                <Grid container spacing={1} alignItems="baseline">
                                    <Grid item xs={12} sm={3} md={2.4} lg={1.8}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#78716C', fontWeight: 400 }}>
                                            Contact
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9} md={9.6} lg={10.2}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#1E293B', fontWeight: 500 }}>
                                            {currentHost.contactPerson || "Priya Menon"} · {currentHost.email} · {currentHost.phone || "+91 98••••120"}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1} alignItems="baseline">
                                    <Grid item xs={12} sm={3} md={2.4} lg={1.8}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#78716C', fontWeight: 400 }}>
                                            Location
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9} md={9.6} lg={10.2}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#1E293B', fontWeight: 500 }}>
                                            {currentHost.location || "Kovalam, Kerala"}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1} alignItems="baseline">
                                    <Grid item xs={12} sm={3} md={2.4} lg={1.8}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#78716C', fontWeight: 400 }}>
                                            Joined
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9} md={9.6} lg={10.2}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#1E293B', fontWeight: 500 }}>
                                            {currentHost.joinedDate || "12 March 2026"}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1} alignItems="baseline">
                                    <Grid item xs={12} sm={3} md={2.4} lg={1.8}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#78716C', fontWeight: 400 }}>
                                            Commission slab
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9} md={9.6} lg={10.2}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#1E293B', fontWeight: 500 }}>
                                            {currentHost.slabDescription || `Premium ${currentHost.slab || '30%'} — 20 pts in block D once the quality gate passes`}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1} alignItems="baseline">
                                    <Grid item xs={12} sm={3} md={2.4} lg={1.8}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#78716C', fontWeight: 400 }}>
                                            Payout method
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9} md={9.6} lg={10.2}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#1E293B', fontWeight: 500 }}>
                                            {currentHost.payoutMethod || "Bank ••4821 · Razorpay Payouts · verified"}
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <Grid container spacing={1} alignItems="baseline">
                                    <Grid item xs={12} sm={3} md={2.4} lg={1.8}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#78716C', fontWeight: 400 }}>
                                            Avg response time
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={9} md={9.6} lg={10.2}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#1E293B', fontWeight: 500 }}>
                                            {currentHost.avgResponseTime || "3h"}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Stack>
                        </Paper>

                        {/* 2. Listings & performance Table */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, md: 2.5 },
                                borderRadius: '8px',
                                border: '1px solid #EAE5D9',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    color: '#2D1810',
                                    mb: 2
                                }}
                            >
                                Listings & performance
                            </Typography>

                            <TableContainer sx={{ border: '1px solid #F0ECE1', borderRadius: '6px', overflow: 'hidden' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#F7F3EA', borderBottom: '1px solid #EBE5D8' }}>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 2 }}>LISTING</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 1.5, textAlign: 'center' }}>STATUS</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 1.5, textAlign: 'center' }}>BOOKINGS</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 1.5, textAlign: 'center' }}>GMV</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 1.5, textAlign: 'center' }}>RATING</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {currentHost.listings.map((item) => (
                                            <TableRow
                                                key={item.id}
                                                sx={{
                                                    '& td': { borderBottom: '1px solid #F5F2EB' },
                                                    '&:last-child td': { borderBottom: 0 },
                                                }}
                                            >
                                                {/* LISTING */}
                                                <TableCell sx={{ py: 1.5, px: 2 }}>
                                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1E293B' }}>
                                                        {item.title}
                                                    </Typography>
                                                </TableCell>

                                                {/* STATUS */}
                                                <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center' }}>
                                                    <Box
                                                        sx={{
                                                            px: 1.2,
                                                            py: 0.3,
                                                            borderRadius: '16px',
                                                            fontSize: '0.7rem',
                                                            fontWeight: 500,
                                                            display: 'inline-block',
                                                            bgcolor: item.status === 'live' ? '#EDF5EB' : '#FFF7E6',
                                                            color: item.status === 'live' ? '#2E7D32' : '#B45309',
                                                            border: item.status === 'live' ? '1px solid #D6EAD2' : '1px solid #FDE68A',
                                                            textTransform: 'lowercase'
                                                        }}
                                                    >
                                                        {item.status}
                                                    </Box>
                                                </TableCell>

                                                {/* BOOKINGS */}
                                                <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center', fontSize: '0.82rem', color: '#1E293B', fontWeight: 500 }}>
                                                    {item.bookings}
                                                </TableCell>

                                                {/* GMV */}
                                                <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center', fontSize: '0.82rem', fontWeight: 500, color: '#1E293B' }}>
                                                    {item.gmv}
                                                </TableCell>

                                                {/* RATING */}
                                                <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center', fontSize: '0.82rem', color: '#1E293B' }}>
                                                    {item.rating || '—'}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        {/* 3. Payout history Table */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, md: 2.5 },
                                borderRadius: '8px',
                                border: '1px solid #EAE5D9',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <Typography
                                    sx={{
                                        fontSize: '0.95rem',
                                        fontWeight: 600,
                                        color: '#2D1810',
                                    }}
                                >
                                    Payout history
                                </Typography>
                                <Box
                                    component="span"
                                    sx={{
                                        border: '1px solid #D97706',
                                        borderRadius: '4px',
                                        px: '5px',
                                        py: '1px',
                                        fontSize: '0.62rem',
                                        fontWeight: 600,
                                        color: '#D97706',
                                        lineHeight: 1
                                    }}
                                >
                                    NEW
                                </Box>
                            </Box>

                            <TableContainer sx={{ border: '1px solid #F0ECE1', borderRadius: '6px', overflow: 'hidden' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow sx={{ bgcolor: '#F7F3EA', borderBottom: '1px solid #EBE5D8' }}>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 2 }}>DATE</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 1.5, textAlign: 'center' }}>BOOKINGS</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 1.5, textAlign: 'center' }}>GROSS</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 1.5, textAlign: 'center' }}>COMMISSION</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 1.5, textAlign: 'center' }}>PAID OUT</TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#78716C', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.25, px: 2, textAlign: 'center' }}>STATUS</TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {currentHost.payouts.map((item) => (
                                            <TableRow
                                                key={item.id}
                                                sx={{
                                                    '& td': { borderBottom: '1px solid #F5F2EB' },
                                                    '&:last-child td': { borderBottom: 0 },
                                                }}
                                            >
                                                {/* DATE */}
                                                <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.82rem', color: '#1E293B' }}>
                                                    {item.date}
                                                </TableCell>

                                                {/* BOOKINGS */}
                                                <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center', fontSize: '0.82rem', color: '#1E293B', fontWeight: 500 }}>
                                                    {item.bookings}
                                                </TableCell>

                                                {/* GROSS */}
                                                <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center', fontSize: '0.82rem', color: '#1E293B' }}>
                                                    {item.gross}
                                                </TableCell>

                                                {/* COMMISSION */}
                                                <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center', fontSize: '0.82rem', color: '#1E293B' }}>
                                                    {item.commission}
                                                </TableCell>

                                                {/* PAID OUT */}
                                                <TableCell sx={{ py: 1.5, px: 1.5, textAlign: 'center', fontSize: '0.84rem', fontWeight: 600, color: '#1E293B' }}>
                                                    {item.paidOut}
                                                </TableCell>

                                                {/* STATUS */}
                                                <TableCell sx={{ py: 1.5, px: 2, textAlign: 'center' }}>
                                                    <Box
                                                        sx={{
                                                            px: 1.2,
                                                            py: 0.3,
                                                            borderRadius: '16px',
                                                            fontSize: '0.7rem',
                                                            fontWeight: 500,
                                                            display: 'inline-block',
                                                            bgcolor: '#EDF5EB',
                                                            color: '#2E7D32',
                                                            border: '1px solid #D6EAD2',
                                                            textTransform: 'lowercase'
                                                        }}
                                                    >
                                                        {item.status}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Stack>
                </Box>

                {/* ── RIGHT SIDE: 2 Cards (Actions + Money Now) in the Right Corner ── */}
                <Box
                    sx={{
                        width: { xs: '100%', md: '280px', lg: '300px' },
                        flexShrink: 0
                    }}
                >
                    <Stack spacing={2.5}>
                        {/* 1. Actions Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, md: 2.5 },
                                borderRadius: '8px',
                                border: '1px solid #EAE5D9',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    color: '#2D1810',
                                    mb: 2
                                }}
                            >
                                Actions
                            </Typography>

                            <Stack spacing={1.2}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => setIsSlabDialogOpen(true)}
                                    disableElevation
                                    disableRipple
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.82rem',
                                        fontWeight: 500,
                                        color: '#2D1810',
                                        border: '1px dashed #D6CFC3',
                                        borderRadius: '6px',
                                        py: 0.9,
                                        boxShadow: 'none',
                                        bgcolor: '#FFFFFF',
                                        '&:hover': {
                                            bgcolor: '#FAF8F5',
                                            borderColor: '#B8AFA0',
                                            borderStyle: 'dashed',
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    Change commission slab
                                </Button>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => setIsMessageDialogOpen(true)}
                                    disableElevation
                                    disableRipple
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.82rem',
                                        fontWeight: 500,
                                        color: '#2D1810',
                                        border: '1px dashed #D6CFC3',
                                        borderRadius: '6px',
                                        py: 0.9,
                                        boxShadow: 'none',
                                        bgcolor: '#FFFFFF',
                                        '&:hover': {
                                            bgcolor: '#FAF8F5',
                                            borderColor: '#B8AFA0',
                                            borderStyle: 'dashed',
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    Message host
                                </Button>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => {
                                        if (onOpenKyc) {
                                            onOpenKyc(currentHost);
                                        } else {
                                            setIsKycDialogOpen(true);
                                        }
                                    }}
                                    disableElevation
                                    disableRipple
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.82rem',
                                        fontWeight: 500,
                                        color: '#2D1810',
                                        border: '1px dashed #D6CFC3',
                                        borderRadius: '6px',
                                        py: 0.9,
                                        boxShadow: 'none',
                                        bgcolor: '#FFFFFF',
                                        '&:hover': {
                                            bgcolor: '#FAF8F5',
                                            borderColor: '#B8AFA0',
                                            borderStyle: 'dashed',
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    View KYC documents
                                </Button>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={() => setIsSuspendDialogOpen(true)}
                                    disableElevation
                                    disableRipple
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '0.82rem',
                                        fontWeight: 500,
                                        color: '#B91C1C',
                                        border: '1px solid #B91C1C',
                                        borderRadius: '6px',
                                        py: 0.9,
                                        boxShadow: 'none',
                                        bgcolor: '#FFFFFF',
                                        '&:hover': {
                                            bgcolor: '#FEF2F2',
                                            borderColor: '#991B1B',
                                            boxShadow: 'none'
                                        }
                                    }}
                                >
                                    Suspend host
                                </Button>
                            </Stack>
                        </Paper>

                        {/* 2. Money now Card */}
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, md: 2.5 },
                                borderRadius: '8px',
                                border: '1px solid #EAE5D9',
                                bgcolor: '#FFFFFF',
                                boxShadow: 'none'
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '0.95rem',
                                    fontWeight: 600,
                                    color: '#2D1810',
                                    mb: 2
                                }}
                            >
                                Money now
                            </Typography>

                            <Stack spacing={1.5}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '0.82rem', color: '#78716C' }}>
                                        In escrow
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B' }}>
                                        {currentHost.money.inEscrow}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '0.82rem', color: '#78716C' }}>
                                        Payable now
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B' }}>
                                        {currentHost.money.payableNow}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography sx={{ fontSize: '0.82rem', color: '#78716C' }}>
                                        Lifetime paid
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#1E293B' }}>
                                        {currentHost.money.lifetimePaid}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Stack>
                </Box>
            </Box>

            {/* ── Change Commission Slab Dialog ── */}
            <Dialog
                open={isSlabDialogOpen}
                onClose={() => setIsSlabDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '8px', border: '1px solid #E2E8F0', p: 1, boxShadow: 'none' }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                        Change Commission Slab
                    </Typography>
                    <IconButton size="small" onClick={() => setIsSlabDialogOpen(false)}>
                        <X size={16} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', mb: 1.5 }}>
                        Select commission rate tier for {currentHost.name}:
                    </Typography>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        value={selectedSlab}
                        onChange={(e) => setSelectedSlab(e.target.value)}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontSize: '0.82rem',
                                borderRadius: '6px',
                                '& fieldset': { borderColor: '#E2E8F0' }
                            }
                        }}
                    >
                        <MenuItem value="15%">Basic — 15%</MenuItem>
                        <MenuItem value="20%">Boosted — 20%</MenuItem>
                        <MenuItem value="25%">Featured — 25%</MenuItem>
                        <MenuItem value="30%">Premium — 30%</MenuItem>
                    </TextField>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIsSlabDialogOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.78rem',
                            color: '#64748B',
                            borderColor: '#E2E8F0',
                            borderRadius: '4px'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSaveSlab}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.78rem',
                            bgcolor: '#803927',
                            color: '#FFFFFF',
                            borderRadius: '4px',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#6A2E1F', boxShadow: 'none' }
                        }}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Message Host Dialog ── */}
            <Dialog
                open={isMessageDialogOpen}
                onClose={() => setIsMessageDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '8px', border: '1px solid #E2E8F0', p: 1, boxShadow: 'none' }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                        Message Host
                    </Typography>
                    <IconButton size="small" onClick={() => setIsMessageDialogOpen(false)}>
                        <X size={16} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '0.78rem', color: '#64748B', mb: 1 }}>
                        Sending to {currentHost.name} ({currentHost.email}):
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Write your message or inquiry..."
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        variant="outlined"
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                fontSize: '0.82rem',
                                borderRadius: '6px',
                                '& fieldset': { borderColor: '#E2E8F0' },
                                '&:hover fieldset': { borderColor: '#CBD5E1' }
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIsMessageDialogOpen(false)}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.78rem',
                            color: '#64748B',
                            borderColor: '#E2E8F0',
                            borderRadius: '4px'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSendMessage}
                        startIcon={<Send size={14} />}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.78rem',
                            bgcolor: '#803927',
                            color: '#FFFFFF',
                            borderRadius: '4px',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#6A2E1F', boxShadow: 'none' }
                        }}
                    >
                        Send Message
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── View KYC Documents Dialog ── */}
            <Dialog
                open={isKycDialogOpen}
                onClose={() => setIsKycDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '8px', border: '1px solid #E2E8F0', p: 1, boxShadow: 'none' }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                        KYC Documents — {currentHost.name}
                    </Typography>
                    <IconButton size="small" onClick={() => setIsKycDialogOpen(false)}>
                        <X size={16} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={1.2}>
                        <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#1E293B' }}>PAN / Tax ID Card</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>Verified with Income Tax records</Typography>
                            </Box>
                            <Box sx={{ px: 1, py: 0.2, bgcolor: '#E8F5E8', color: '#2E7D32', fontSize: '0.68rem', borderRadius: '4px', fontWeight: 600 }}>
                                Verified
                            </Box>
                        </Box>

                        <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#1E293B' }}>GSTIN Registration Certificate</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>GST Active & Compliant</Typography>
                            </Box>
                            <Box sx={{ px: 1, py: 0.2, bgcolor: '#E8F5E8', color: '#2E7D32', fontSize: '0.68rem', borderRadius: '4px', fontWeight: 600 }}>
                                Verified
                            </Box>
                        </Box>

                        <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#1E293B' }}>Bank Account Proof (HDFC ••4821)</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#64748B' }}>Razorpay Payouts Verified</Typography>
                            </Box>
                            <Box sx={{ px: 1, py: 0.2, bgcolor: '#E8F5E8', color: '#2E7D32', fontSize: '0.68rem', borderRadius: '4px', fontWeight: 600 }}>
                                Verified
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIsKycDialogOpen(false)}
                        sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#1E293B', borderColor: '#E2E8F0' }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Suspend Host Confirmation Dialog ── */}
            <Dialog
                open={isSuspendDialogOpen}
                onClose={() => setIsSuspendDialogOpen(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '8px', border: '1px solid #E2E8F0', p: 1, boxShadow: 'none' }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AlertTriangle size={18} color="#DC2626" />
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#1E293B' }}>
                        Suspend Host Account
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: '0.8rem', color: '#64748B' }}>
                        Are you sure you want to suspend <strong>{currentHost.name}</strong>? Their retreat listings will be hidden from the public directory.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setIsSuspendDialogOpen(false)}
                        sx={{ textTransform: 'none', fontSize: '0.78rem', color: '#64748B', borderColor: '#E2E8F0' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleSuspendToggle}
                        sx={{
                            textTransform: 'none',
                            fontSize: '0.78rem',
                            bgcolor: '#DC2626',
                            color: '#FFFFFF',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' }
                        }}
                    >
                        Confirm Suspension
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Toast Snackbar ── */}
            <Snackbar
                open={toastOpen}
                autoHideDuration={4000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setToastOpen(false)}
                    severity={toastSeverity}
                    sx={{
                        bgcolor: '#1E293B',
                        color: '#FFFFFF',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        boxShadow: 'none',
                        '& .MuiAlert-icon': { color: '#FFBD65' }
                    }}
                >
                    {toastMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default HostProfile;
